import { Icon as NewIcon } from '@buggy/shared'
import { Dropdown, Flex, notification, Tag, Tooltip } from 'antd'
import type { ItemType } from 'antd/es/menu/hooks/useItems'
import { compact, isEmpty, isNull } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import {
  CSSProperties,
  FunctionComponent,
  ReactNode,
  RefObject,
  useRef,
} from 'react'
import Highlighter from 'react-highlight-words'
import styled, {
  css,
  FlattenSimpleInterpolation,
  useTheme,
} from 'styled-components'
import MentionKolList, {
  MentionKolListProps,
} from '@/components/collections/table/grid/mention-kol-list'
import PostImage from '@/components/common/image/post-image'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import PostCardStatistic from '@/components/common/post/post-card-statistic'
import { Icon } from '@/components/custom-icon'
import SponsoredTag from '@/components/kol/content/sponsored-tag'
import UnsafeNewTabLink from '@/components/public-page/footer/components/unsafe-new-tab-link'
import routes from '@/hooks/use-authorization/routes'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useIntl } from '@/i18n/hooks/use-intl'
import { FeedbackType } from '@/types/api/feedback'
import { Nullable } from '@/types/nullable'
import { PostCollectionPost } from '@/types/schema/post-collection'
import { ampli } from '@/utils/ampli'
import {
  convertPlatformShortcode,
  PlatformShortcode,
} from '@/utils/convert-platform'
import { keywordsTruncate } from '@/utils/keywords-truncator'

export const POST_CARD_CONTENT_PADDING = 16

interface CardProps {
  kolUuid: string
  kolName: string
  kolIsVerified?: boolean
  kolIsRadarStar?: boolean
  postUrl: string
  thumbnailUrls?: string[]
  publishedAt?: Nullable<string>
  title: string
  mentionKols?: MentionKolListProps['kols']
  titleLineClamp?: number
  likeCount?: Nullable<number>
  commentCount?: Nullable<number>
  viewCount?: Nullable<number>
  playCount?: Nullable<number>
  shareCount?: Nullable<number>
  estimatedReachCount?: Nullable<number>
  isAdvertorial?: boolean
  canGiveFeedback?: boolean
  platformShortCode?: PlatformShortcode
  showPlatformIcon?: boolean
  status?: PostCollectionPost['recorded_status']
  className?: string
  imageActionBlock?: ReactNode
  actionBlock?: ReactNode
  imageActionBlockDisplayTrigger?: 'hover' | 'always'
  // caveat: 需使用 api 回傳拆解後的 keywords，直接取 query string 會包含搜尋語法: e.g. AND, OR , {} 等
  keywords?: string[]
  keywordsHighlightStyle?: CSSProperties
  customizedTagNames?: string[]
  showKolInfo?: boolean
  from?: string
  onPostClick?: (_: undefined, ref: RefObject<HTMLElement>) => void
  onPostRefetch?: () => void
  onTagClick?: (tagName: string) => void
  onClickKolInfo?: () => void
  onClickMaximumMentionKol?: MentionKolListProps['onClickMaximumMentionKol']
  isDemo?: boolean
  /**
   * Content to render in the bottom of the card
   */
  extra?: ReactNode
  isDisableLike?: boolean
}

const NOT_SUPPORTED_PLATFORM_TITLE_LIST = ['', 'Other', 'All']

const PostCard: FunctionComponent<CardProps> = ({
  kolUuid,
  kolName,
  postUrl,
  thumbnailUrls,
  publishedAt,
  title,
  titleLineClamp = 4,
  mentionKols,
  likeCount,
  commentCount,
  viewCount,
  playCount,
  shareCount,
  estimatedReachCount,
  isAdvertorial = false,
  canGiveFeedback = true,
  platformShortCode,
  showPlatformIcon = true,
  status = 'recorded',
  className,
  actionBlock,
  imageActionBlock,
  imageActionBlockDisplayTrigger = 'hover',
  showKolInfo = true,
  keywords = [],
  keywordsHighlightStyle,
  customizedTagNames,
  from,
  onPostClick = (): void => {},
  onPostRefetch = (): void => {},
  onTagClick = (): void => {},
  onClickMaximumMentionKol = (): void => {},
  onClickKolInfo = (): void => {},
  extra,
  isDemo,
  isDisableLike = false,
  kolIsVerified = false,
  kolIsRadarStar = false,
}) => {
  const { formatMessage, formatNumber, formatDate } = useIntl()
  const theme = useTheme()
  const platform = convertPlatformShortcode(platformShortCode)
  const cardWrapperRef = useRef<HTMLDivElement>(null)
  const { getCustomizedTagLocalizedName } = useCustomizedTag()

  // 這些平台目前不支援，如果出現就不顯示，防止出現未預期畫面
  if (
    NOT_SUPPORTED_PLATFORM_TITLE_LIST.includes(platform.title) ||
    !platform.icon
  ) {
    return null
  }

  const handlePostClick = (): void => {
    onPostClick(undefined, cardWrapperRef)
  }

  const dropdownMenuItems: ItemType[] = compact([
    isAdvertorial
      ? {
          key: FeedbackType.Advertorial,
          icon: <Icon type='exclamation-circle' />,
          label: formatMessage({
            id: 'kol:content_report_not_advertorial',
          }),
          onClick: (): void => {
            ampli.clickReportNonSponsoredPost({
              postLink: postUrl,
              kolId: kolUuid,
              platform: platformShortCode,
            })
            notification.success({
              message: formatMessage({
                id: 'irm:report_kols_not_in_system_success',
              }),
              placement: 'bottomRight',
            })
          },
        }
      : {
          key: FeedbackType.Advertorial,
          icon: <Icon type='elPayment' />,
          label: formatMessage({
            id: 'kol:content_report_is_advertorial',
          }),
          onClick: (): void => {
            ampli.clickReportSponsoredPost({
              postLink: postUrl,
              kolId: kolUuid,
              platform: platformShortCode,
            })
            notification.success({
              message: formatMessage({
                id: 'irm:report_kols_not_in_system_success',
              }),
              placement: 'bottomRight',
            })
          },
        },
    !isEmpty(customizedTagNames)
      ? {
          key: FeedbackType.CustomizedTag,
          icon: <Icon type='exclamation-circle' />,
          label: formatMessage({
            id: 'kol:content_report_wrong_customized_tag',
          }),
          onClick: (): void => {
            ampli.reportContentTypeIsIncorrect({
              kolId: kolUuid,
              postLink: postUrl,
              platform: platformShortCode,
            })
            notification.success({
              message: formatMessage({
                id: 'irm:report_kols_not_in_system_success',
              }),
              placement: 'bottomRight',
            })
          },
        }
      : undefined,
    {
      key: 'close',
      icon: (
        <Flex align='center' justify='center'>
          <NewIcon fontSize={14} name='x' />
        </Flex>
      ),
      label: formatMessage({
        id: 'kol:content_cancel_report_advertorial',
      }),
      onClick: (menuInfo): void => {
        menuInfo.domEvent.preventDefault()
        menuInfo.domEvent.stopPropagation()
      },
    },
  ])

  return (
    <Wrapper
      className={className}
      imageActionBlockDisplayTrigger={imageActionBlockDisplayTrigger}
      ref={cardWrapperRef}
    >
      <div
        role='button'
        style={{ position: 'relative' }}
        onClick={handlePostClick}
      >
        <PostImage
          height={178}
          kolUuid={kolUuid}
          thumbnailUrls={thumbnailUrls}
        />
        {isAdvertorial && <SponsoredTag />}
        {!isNil(estimatedReachCount) && (
          <EstimatedReachCount>
            {formatMessage(
              { id: 'post:estimated_reach_count' },
              {
                estimatedReachCount: formatNumber(estimatedReachCount, {
                  isCompactDisplay: true,
                }),
              },
            )}
          </EstimatedReachCount>
        )}
        <ImageActionBlock
          onClick={(e): void => {
            e.stopPropagation()
          }}
        >
          {imageActionBlock}
        </ImageActionBlock>
      </div>
      <PostCardContent>
        <PostCardOtherInfo>
          <div>
            {showPlatformIcon && platform.sharedIconName && (
              <NewIcon fontSize={16} name={platform.sharedIconName} />
            )}
            <DateWrapper>
              {publishedAt ? formatDate(publishedAt) : '-'}
            </DateWrapper>
          </div>
          {(!isNull(actionBlock) || canGiveFeedback) && (
            <ActionWrapper>
              {actionBlock}
              {canGiveFeedback && (
                <Dropdown
                  disabled={isDemo}
                  menu={{
                    items: dropdownMenuItems,
                  }}
                  trigger={['click']}
                >
                  <Tooltip
                    placement='bottom'
                    title={formatMessage({ id: 'general:report_tooltip' })}
                  >
                    <ReportSponsoredPostTrigger>
                      <NewIcon fontSize={20} name='more-vertical' />
                    </ReportSponsoredPostTrigger>
                  </Tooltip>
                </Dropdown>
              )}
            </ActionWrapper>
          )}
        </PostCardOtherInfo>
        <PostCardTitle lineClamp={titleLineClamp} onClick={handlePostClick}>
          {keywords.length > 0 ? (
            <Highlighter
              autoEscape
              highlightStyle={
                keywordsHighlightStyle ?? {
                  backgroundColor: 'unset',
                  color: theme.colors.brand.primary,
                  padding: 0,
                }
              }
              searchWords={keywords}
              textToHighlight={keywordsTruncate(title, keywords)}
            />
          ) : (
            title
          )}
        </PostCardTitle>
        {((): ReactNode => {
          switch (status) {
            case 'recorded':
              return (
                <PostCardStatistic
                  commentCount={commentCount}
                  isDisableLike={isDisableLike}
                  likeCount={likeCount}
                  platformShortCode={platformShortCode}
                  playCount={playCount}
                  shareCount={shareCount}
                  viewCount={viewCount}
                />
              )
            case 'updating':
              return (
                <Updating>
                  {formatMessage({ id: 'general:notify_data_updating' })}
                </Updating>
              )
            case 'update-failed':
              return (
                <UpdateFailed>
                  {formatMessage({ id: 'general:notify_data_update_failed' })}
                  {formatMessage({ id: 'general:comma' })}
                  <span onClick={onPostRefetch}>
                    {formatMessage({ id: 'general:refresh_btn' })}
                  </span>
                </UpdateFailed>
              )
            default: {
              const never: never = status
              return never
            }
          }
        })()}
        <Flex vertical gap={16}>
          {customizedTagNames && customizedTagNames.length > 0 && (
            <CustomizedTags>
              {customizedTagNames.map((customizedTagName) => (
                <Tag
                  key={customizedTagName}
                  onClick={(): void => onTagClick(customizedTagName)}
                >
                  {getCustomizedTagLocalizedName(customizedTagName)}
                </Tag>
              ))}
            </CustomizedTags>
          )}
          {showKolInfo && (
            <KolInfo
              as={UnsafeNewTabLink}
              href={
                isDemo
                  ? undefined
                  : routes.kolId.stringifyUrl(
                      {
                        kolId: kolUuid,
                      },
                      { from },
                    )
              }
              onClick={onClickKolInfo}
            >
              <KolThumbnail
                imageSize={24}
                isRadarStar={kolIsRadarStar}
                isVerified={kolIsVerified}
                kolUuid={kolUuid}
              />
              {kolName}
            </KolInfo>
          )}
        </Flex>
        {mentionKols && mentionKols.length > 0 && (
          <MentionKolList
            isDemo={isDemo}
            kols={mentionKols}
            onClickMaximumMentionKol={onClickMaximumMentionKol}
          />
        )}
        {extra}
      </PostCardContent>
    </Wrapper>
  )
}

const ImageActionBlock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  > * {
    pointer-events: all;
  }
`

const DateWrapper = styled.div`
  font-size: 12px;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};

  > .anticon {
    margin-right: 4px;
  }
`

export const PostCardOtherInfo = styled.div`
  display: flex;
  justify-content: space-between;

  > div {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`

export const PostCardTitle = styled.div<{ lineClamp: number }>`
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme }): string => theme.colors.text.primary};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({ lineClamp }): number => lineClamp};
  -webkit-box-orient: vertical;
  white-space: normal;
`

const UpdateFailed = styled.div`
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.text.secondary};

  > span {
    color: ${({ theme }): string => theme.colors.brand.primary};
    text-decoration-line: underline;
    cursor: pointer;
  }
`
const Updating = styled.div`
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
`

const KolInfo = styled.a`
  display: flex;
  align-items: center;
  font-size: 12px;

  &,
  &:hover {
    color: ${({ theme }): string => theme.colors.text.secondary};
  }
`

export const PostCardContent = styled.div`
  padding: ${POST_CARD_CONTENT_PADDING}px;
  background: ${({ theme }): string => theme.colors.background.white};
  display: flex;
  flex-direction: column;

  ${PostCardOtherInfo} {
    margin-bottom: 6px;
  }
  ${PostCardTitle} {
    margin-bottom: 8px;
  }

  ${PostCardStatistic},${Updating},${UpdateFailed} {
    flex: 1 1 auto;
    align-content: flex-start;
    margin-bottom: 12px;
  }
`

const Wrapper = styled.div<Pick<CardProps, 'imageActionBlockDisplayTrigger'>>`
  display: flex;
  height: 100%;
  flex-direction: column;
  line-height: 1.4;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  ${KolThumbnail} {
    margin-right: 4px;
  }
  ${PostCardContent} {
    flex: 1 1 auto;
  }

  ${({ imageActionBlockDisplayTrigger }): FlattenSimpleInterpolation => {
    switch (imageActionBlockDisplayTrigger) {
      case 'hover':
        return css`
          ${ImageActionBlock} {
            display: none;
          }
          &:hover {
            ${ImageActionBlock} {
              display: block;
            }
          }
        `
      case 'always':
      default:
        return css``
    }
  }}
`

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  line-height: 1px;
`

const ReportSponsoredPostTrigger = styled.div`
  cursor: pointer;
  margin-left: 5px;
`

const EstimatedReachCount = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 4px 15px;
  background: rgb(
    from ${({ theme }): string => theme.colors.gray10} r g b / 40%
  );
  color: ${({ theme }): string => theme.colors.white};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const CustomizedTags = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: auto;

  mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 1) 80%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 1) 80%,
    rgba(0, 0, 0, 0) 100%
  );

  .ant-tag {
    margin-right: 4px;
    cursor: pointer;
  }
`

export default styled(PostCard)``
